import { GDTFConverter, GDTFUtil } from "@coten-inc/utils";
import { astronomicalYearToGdtf, gdtfYearToAstronomical, gregorianToJDN } from "@coten-inc/utils/dist/gdtf/parser";

import { GDTF } from "~/common/components/timeline";

type ParsedGdtfRange = {
  fromYear: number | null;
  toYear: number | null;
  fromJdn: number | null;
  toJdn: number | null;
  /** true when both bounds are individually valid but from > to */
  inverted: boolean;
  /** true when at least one bound parsed successfully */
  hasFilter: boolean;
};

const EMPTY: ParsedGdtfRange = {
  fromYear: null,
  toYear: null,
  fromJdn: null,
  toJdn: null,
  inverted: false,
  hasFilter: false,
};

export const formatJdnAsGdtfDate = (jdn: number | null): string => {
  if (jdn === null) return "—";

  const a = jdn + 32_044;
  const b = Math.floor((4 * a + 3) / 146_097);
  const c = a - Math.floor((146_097 * b) / 4);
  const d = Math.floor((4 * c + 3) / 1_461);
  const e = c - Math.floor((1_461 * d) / 4);
  const m = Math.floor((5 * e + 2) / 153);
  const day = e - Math.floor((153 * m + 2) / 5) + 1;
  const month = m + 3 - 12 * Math.floor(m / 10);
  const astronomicalYear = 100 * b + d - 4_800 + Math.floor(m / 10);
  const year = astronomicalYearToGdtf(astronomicalYear);

  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

const getBoundaryJdn = (gdtf: string, boundary: "start" | "end"): number | null => {
  const terms = GDTFUtil.parseGDTF(gdtf).ok;
  if (!terms?.length) return null;

  const term = boundary === "start" ? terms[0] : terms[terms.length - 1];
  const [year, month, day] = term.dateArray ?? [];
  if (year === undefined || month === undefined || day === undefined) return null;

  return gregorianToJDN(gdtfYearToAstronomical(year), month, day);
};

export const resolveGdtfJdnRange = (gdtfFrom?: string, gdtfTo?: string) => {
  const fromJdn = gdtfFrom ? getBoundaryJdn(gdtfFrom, "start") : null;
  const hasExplicitUpperBound = Boolean(gdtfTo?.trim());
  const upperBound = hasExplicitUpperBound ? gdtfTo : gdtfFrom;
  const parsedToJdn = upperBound ? getBoundaryJdn(upperBound, "end") : null;
  const toJdn = hasExplicitUpperBound && parsedToJdn === null ? fromJdn : parsedToJdn;

  return { fromJdn, toJdn };
};

/**
 * Parse a GDTF from/to pair once, extracting both year and JDN values.
 * Invalid or missing inputs produce null for that bound.
 */
export const parseGdtfRange = (gdtfFrom?: string, gdtfTo?: string): ParsedGdtfRange => {
  if (!gdtfFrom && !gdtfTo) return EMPTY;

  const parsedFrom = gdtfFrom ? new GDTF(gdtfFrom, true) : null;
  const parsedTo = gdtfTo ? new GDTF(gdtfTo, false) : null;

  const fromYear = parsedFrom?.valid ? parsedFrom.year : null;
  const toYear = parsedTo?.valid ? parsedTo.year : null;

  const fromJdn = gdtfFrom ? (new GDTFConverter(gdtfFrom).getJDN() ?? null) : null;
  const toJdn = gdtfTo ? (new GDTFConverter(gdtfTo).getJDN() ?? null) : null;

  const hasFilter = fromYear != null || toYear != null;
  const inverted = fromJdn != null && toJdn != null && fromJdn > toJdn;

  return { fromYear, toYear, fromJdn, toJdn, inverted, hasFilter };
};

/**
 * Compute a year range with padding, suitable for timeline chunk loading.
 * Returns null when there is no usable filter or the range is inverted.
 */
export const gdtfYearRangeWithPadding = (parsed: ParsedGdtfRange, defaultSpan: number): [number, number] | null => {
  if (!parsed.hasFilter || parsed.inverted) return null;
  const lo = parsed.fromYear ?? parsed.toYear! - defaultSpan;
  const hi = parsed.toYear ?? parsed.fromYear! + defaultSpan;
  const padding = Math.max(50, Math.abs(hi - lo) * 0.1);
  return [lo - padding, hi + padding];
};
